import { useState, useEffect } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { App } from 'antd';
import { auth } from '../services/auth';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: 'login' | 'register';
  onSuccess?: () => void;
}

// 校验规则
const validateEmail = (email: string): string => {
  if (!email) return '请输入邮箱地址';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return '请输入有效的邮箱地址';
  return '';
};

const validatePassword = (password: string): string => {
  if (!password) return '请输入密码';
  if (password.length < 8) return '密码长度至少为8位';
  if (password.length > 16) return '密码长度不能超过16位';
  if (!/[a-z]/.test(password)) return '密码必须包含小写字母';
  if (!/[A-Z]/.test(password)) return '密码必须包含大写字母';
  if (!/[0-9]/.test(password)) return '密码必须包含数字';
  return '';
};

const validateNickname = (nickname: string): string => {
  if (!nickname) return '请输入昵称';
  if (nickname.length > 20) return '昵称长度不能超过20位';
  return '';
};

const validateConfirmPassword = (
  password: string,
  confirmPassword: string,
): string => {
  if (!confirmPassword) return '请确认密码';
  if (confirmPassword !== password) return '两次输入的密码不一致';
  return '';
};

export default function AuthModal({
  isOpen,
  onClose,
  defaultMode = 'login',
  onSuccess,
}: AuthModalProps) {
  const { message } = App.useApp();
  const [mode, setMode] = useState<'login' | 'register'>(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 阻止背景页面滚动
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  // 字段级校验错误
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    nickname?: string;
  }>({});

  // 已触碰的字段
  const [touched, setTouched] = useState<{
    email?: boolean;
    password?: boolean;
    confirmPassword?: boolean;
    nickname?: boolean;
  }>({});

  // 当 defaultMode 或 isOpen 变化时，更新 mode
  useEffect(() => {
    if (isOpen) {
      setMode(defaultMode);
      // 重置表单状态
      setError('');
      setFieldErrors({});
      setTouched({});
      // 清空已填写的内容
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setNickname('');
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [defaultMode, isOpen]);

  if (!isOpen) return null;

  // 实时校验单个字段
  const validateField = (
    field: 'email' | 'password' | 'confirmPassword' | 'nickname',
    value: string,
  ) => {
    let errorMsg = '';
    switch (field) {
      case 'email':
        errorMsg = validateEmail(value);
        break;
      case 'password':
        errorMsg = validatePassword(value);
        break;
      case 'confirmPassword':
        errorMsg =
          mode === 'register' ? validateConfirmPassword(password, value) : '';
        break;
      case 'nickname':
        errorMsg = mode === 'register' ? validateNickname(value) : '';
        break;
    }
    setFieldErrors((prev) => ({ ...prev, [field]: errorMsg }));
    return errorMsg === '';
  };

  // 处理字段变化
  const handleFieldChange = (
    field: 'email' | 'password' | 'confirmPassword' | 'nickname',
    value: string,
  ) => {
    switch (field) {
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        // 如果确认密码已填写，重新校验确认密码
        if (confirmPassword && touched.confirmPassword) {
          validateField('confirmPassword', confirmPassword);
        }
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
      case 'nickname':
        setNickname(value);
        break;
    }
    // 如果字段已被触碰，实时校验
    if (touched[field]) {
      validateField(field, value);
    }
  };

  // 处理字段失焦 - 仅标记为已触碰，不进行实时校验
  const handleFieldBlur = (
    field: 'email' | 'password' | 'confirmPassword' | 'nickname',
  ) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // 校验整个表单
  const validateForm = (): boolean => {
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    const confirmPasswordError =
      mode === 'register'
        ? validateConfirmPassword(password, confirmPassword)
        : '';
    const nicknameError = mode === 'register' ? validateNickname(nickname) : '';

    setFieldErrors({
      email: emailError,
      password: passwordError,
      ...(mode === 'register' && { confirmPassword: confirmPasswordError }),
      ...(mode === 'register' && { nickname: nicknameError }),
    });
    setTouched({
      email: true,
      password: true,
      ...(mode === 'register' && { confirmPassword: true }),
      ...(mode === 'register' && { nickname: true }),
    });

    return (
      !emailError && !passwordError && !confirmPasswordError && !nicknameError
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 表单校验
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let result;
      if (mode === 'login') {
        result = await auth.login(email, password);
      } else {
        result = await auth.register(email, password, nickname);
      }

      if (result.success) {
        if (mode === 'register') {
          // 注册成功：关闭弹窗并触发回调（注册接口已自动完成登录）
          message.success('注册成功');
          onClose();
          // 调用成功回调，通知父组件更新状态
          onSuccess?.();
        } else {
          // 登录成功
          message.success('登录成功');
          onClose();
          // 调用成功回调，通知父组件更新状态
          onSuccess?.();
        }
      } else {
        setError(result.message);
      }
    } catch {
      setError('操作失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    // 清空错误和校验状态
    setError('');
    setFieldErrors({});
    setTouched({});
    // 清空已填写的内容
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setNickname('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onWheel={(e) => e.preventDefault()}
        onTouchMove={(e) => e.preventDefault()}
      />

      {/* 弹窗 */}
      <div className="relative w-full max-w-md bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden animate-scale-in">
        {/* 顶部装饰 */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-emerald-500 to-amber-500" />

        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-4 right-4 text-slate-400 hover:text-amber-400 hover:bg-amber-500/20 rounded-lg p-1 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          {/* 标题 */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">
              {mode === 'login' ? '欢迎回来' : '创建账号'}
            </h2>
            <p className="text-slate-400 text-sm">
              {mode === 'login'
                ? '解锁专属面试报告，追踪你的成长轨迹'
                : '开启你的AI面试之旅，斩获心仪Offer'}
            </p>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* 表单 */}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* 昵称（仅注册） */}
            {mode === 'register' && (
              <div>
                <label className="block text-slate-300 text-sm mb-2">
                  昵称
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) =>
                      handleFieldChange('nickname', e.target.value)
                    }
                    onBlur={() => handleFieldBlur('nickname')}
                    placeholder="请输入昵称"
                    className={`w-full pl-10 pr-4 py-3 bg-slate-700/50 border rounded-lg text-white placeholder-slate-500 focus:outline-none transition-all duration-200 cursor-text
                      ${
                        fieldErrors.nickname && touched.nickname
                          ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                          : 'border-slate-600 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20'
                      }`}
                  />
                </div>
                {/* 昵称校验错误提示 */}
                {fieldErrors.nickname && touched.nickname && (
                  <p className="mt-1.5 text-red-400 text-xs flex items-center gap-1 animate-fade-in">
                    <svg
                      className="w-3.5 h-3.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {fieldErrors.nickname}
                  </p>
                )}
              </div>
            )}

            {/* 邮箱 */}
            <div>
              <label className="block text-slate-300 text-sm mb-2">邮箱</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  onBlur={() => handleFieldBlur('email')}
                  placeholder="请输入邮箱"
                  className={`w-full pl-10 pr-4 py-3 bg-slate-700/50 border rounded-lg text-white placeholder-slate-500 focus:outline-none transition-all duration-200 cursor-text
                    ${
                      fieldErrors.email && touched.email
                        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        : 'border-slate-600 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20'
                    }`}
                />
              </div>
              {/* 邮箱校验错误提示 */}
              {fieldErrors.email && touched.email && (
                <p className="mt-1.5 text-red-400 text-xs flex items-center gap-1 animate-fade-in">
                  <svg
                    className="w-3.5 h-3.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {fieldErrors.email}
                </p>
              )}
            </div>

            {/* 密码 */}
            <div>
              <label className="block text-slate-300 text-sm mb-2">密码</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) =>
                    handleFieldChange('password', e.target.value)
                  }
                  onBlur={() => handleFieldBlur('password')}
                  placeholder="请输入密码"
                  className={`w-full pl-10 pr-12 py-3 bg-slate-700/50 border rounded-lg text-white placeholder-slate-500 focus:outline-none transition-all duration-200 cursor-text
                    ${
                      fieldErrors.password && touched.password
                        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        : 'border-slate-600 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20'
                    }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 cursor-pointer transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {/* 密码校验错误提示 */}
              {fieldErrors.password && touched.password && (
                <p className="mt-1.5 text-red-400 text-xs flex items-center gap-1 animate-fade-in">
                  <svg
                    className="w-3.5 h-3.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {fieldErrors.password}
                </p>
              )}
            </div>

            {/* 确认密码（仅注册） */}
            {mode === 'register' && (
              <div>
                <label className="block text-slate-300 text-sm mb-2">
                  确认密码
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) =>
                      handleFieldChange('confirmPassword', e.target.value)
                    }
                    onBlur={() => handleFieldBlur('confirmPassword')}
                    placeholder="请再次输入密码"
                    className={`w-full pl-10 pr-12 py-3 bg-slate-700/50 border rounded-lg text-white placeholder-slate-500 focus:outline-none transition-all duration-200 cursor-text
                      ${
                        fieldErrors.confirmPassword && touched.confirmPassword
                          ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                          : 'border-slate-600 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20'
                      }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 cursor-pointer transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {/* 确认密码校验错误提示 */}
                {fieldErrors.confirmPassword && touched.confirmPassword && (
                  <p className="mt-1.5 text-red-400 text-xs flex items-center gap-1 animate-fade-in">
                    <svg
                      className="w-3.5 h-3.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {fieldErrors.confirmPassword}
                  </p>
                )}
              </div>
            )}

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500
                text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 cursor-pointer"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {mode === 'login' ? '登录中' : '注册中'}
                </span>
              ) : mode === 'login' ? (
                '登录'
              ) : (
                '注册'
              )}
            </button>
          </form>

          {/* 切换模式 */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              {mode === 'login' ? '还没有账号？' : '已有账号？'}
              <button
                onClick={switchMode}
                className="ml-1 text-blue-400 hover:text-blue-300 font-medium cursor-pointer transition-colors"
              >
                {mode === 'login' ? '立即注册' : '立即登录'}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* 动画样式 */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }

        /* 覆盖浏览器自动填充样式 */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px rgba(55, 65, 81, 0.5) inset !important;
          -webkit-text-fill-color: white !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>
    </div>
  );
}
